import { useEffect } from 'react';

import { TourTooltipCard } from '@/components/TourTooltipCard';
import { VStack } from '@/components/ui/vstack';
import { useTourCallout } from '@/hooks/useTourCallout';
import { CALLOUT_KEYS } from '@/services/userCallouts';

export const QUESTION_GROUP_TIP_STEP_ONE =
  'Inquiry groups sent to several experts are grouped. Tap an inquiry group to see each expert.';

export const QUESTION_GROUP_TIP_STEP_TWO = 'Tap an expert to open that conversation.';

type QuestionGroupTipProps = {
  step: 1 | 2;
  onNext: () => void;
  onBack: () => void;
};

/**
 * The one-time, two-step tip under the first inquiry group that has several experts. It sits in the list's
 * flow beneath the card, so it never covers another card, and it follows the app's tour card. Seen
 * is the server-backed callout every tour uses, so finishing or skipping it once hides it on every
 * device.
 */
export function QuestionGroupTip({ step, onNext, onBack }: QuestionGroupTipProps) {
  const { shouldShow, markShown, markSeen } = useTourCallout({
    key: CALLOUT_KEYS.INBOX_QUESTION_GROUP_CARDS_INTRO,
    enabled: true,
  });

  useEffect(() => {
    if (shouldShow) {
      markShown();
    }
  }, [shouldShow, markShown]);

  if (!shouldShow) {
    return null;
  }

  return (
    <VStack testID="question-group-cards-tip" className="mt-2 pl-3">
      {step === 1 ? (
        <TourTooltipCard
          text={QUESTION_GROUP_TIP_STEP_ONE}
          primaryLabel="Next"
          onPrimary={onNext}
          onSkip={markSeen}
        />
      ) : (
        <TourTooltipCard
          text={QUESTION_GROUP_TIP_STEP_TWO}
          primaryLabel="Got it"
          onPrimary={markSeen}
          onBack={onBack}
        />
      )}
    </VStack>
  );
}
